import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  type = 'button',
  onClick,
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  className,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    primary: 'bg-primary-500 text-white border border-primary-500 hover:bg-primary-600 active:bg-primary-700 focus:ring-primary-400 shadow-sm hover:shadow-md',
    'primary-filled': 'bg-primary-600 text-white border border-primary-600 hover:bg-primary-700 active:bg-primary-800 focus:ring-primary-400 shadow-sm hover:shadow-md',
    secondary: 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50 active:bg-neutral-100 focus:ring-neutral-300 dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700 dark:hover:bg-neutral-700',
    ghost: 'bg-transparent text-neutral-600 border border-transparent hover:bg-neutral-100 active:bg-neutral-200 focus:ring-neutral-300 dark:text-neutral-300 dark:hover:bg-neutral-800',
    danger: 'bg-red-500 text-white border border-red-500 hover:bg-red-600 active:bg-red-700 focus:ring-red-400 shadow-sm',
    success: 'bg-emerald-500 text-white border border-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 focus:ring-emerald-400 shadow-sm',
  };

  const sizeStyles = {
    sm: 'text-sm px-3 py-2 h-9',
    md: 'text-sm px-4 py-2.5 h-10',
    lg: 'text-base px-6 py-3 h-12',
  };

  const buttonClasses = clsx(baseStyles, variantStyles[variant], sizeStyles[size], fullWidth && 'w-full', className);

  const LoadingSpinner = () => (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={buttonClasses}
      whileHover={!disabled && !loading ? { scale: 1.01 } : {}}
      whileTap={!disabled && !loading ? { scale: 0.98 } : {}}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {!loading && icon && iconPosition === 'left' && <span className="inline-flex">{icon}</span>}
      {children}
      {!loading && icon && iconPosition === 'right' && <span className="inline-flex">{icon}</span>}
    </motion.button>
  );
};

export default Button;
